import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import {
	BehaviorSubject,
	debounceTime,
	distinctUntilChanged,
	EMPTY,
	finalize,
	Observable,
	Subject,
	switchMap,
	take,
	takeUntil,
} from 'rxjs'
import { TuiIcon, TuiLoader } from '@taiga-ui/core'
import { TuiBadge, TuiHighlight } from '@taiga-ui/kit'
import { WatchListService } from '@modules/watch-list-module/services/watch-list.service'
import { WatchListResponseInterface } from '@modules/watch-list-module/interfaces/watch-list-response.interface'
import { HttpErrorResponse } from '@angular/common/http'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { WatchListAssetInterface } from '@modules/watch-list-module/interfaces/watch-list-asset.interface'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { AlertService } from '@shared/services/alert.service'
import { SearchAssetsItemInterface } from '@modules/stocks-module/general/interfaces/search-assets-item.interface'
import { SearchAssetsPaginationInterface } from '@modules/stocks-module/general/interfaces/search-assets-pagination.interface'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { WatchListHelperService } from '@modules/watch-list-module/services/watch-list-helper.service'
@Component({
	selector: 'app-table-search-assets',
	templateUrl: './table-search-assets.component.html',
	styleUrl: './table-search-assets.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TuiBadge, TuiHighlight, TuiLoader, TranslatePipe, TuiIcon],
})
export class TableSearchAssetsComponent implements OnInit, OnChanges, OnDestroy {
	public searchValue: InputSignal<string | null> = input<string | null>(null)
	public onSelectAsset: OutputEmitterRef<SearchAssetsItemInterface> = output<SearchAssetsItemInterface>()

	private _destroy$: Subject<void> = new Subject()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _watchListService: WatchListService = inject<WatchListService>(WatchListService)
	private readonly _watchListHelperService: WatchListHelperService =
		inject<WatchListHelperService>(WatchListHelperService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)

	private _searchQuery$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)

	protected searchAssetsResponse: WritableSignal<SearchAssetsPaginationInterface | null> = signal(null)
	protected isStartedSearch: WritableSignal<boolean> = signal(false)
	protected isLoadingAssets: WritableSignal<boolean> = signal(false)
	protected watchList: WritableSignal<WatchListResponseInterface | null> = signal(null)
	protected symbolsLoading: WritableSignal<string[]> = signal([])
	private _launchWatchList(): void {
		const hasWatchList: boolean = !!this._watchListHelperService.watchList$.getValue()

		this._tradingLauncherService
			.launchWatchList$(!hasWatchList)
			.pipe(takeUntil(this._destroy$))
			.subscribe(([watchList]: [WatchListResponseInterface | null, boolean]): void => {
				this.watchList.set(watchList)
			})
	}

	private _searchAssets$(value: string): Observable<SearchAssetsPaginationInterface> {
		this.isStartedSearch.set(true)
		this.isLoadingAssets.set(true)

		return this._stocksService.searchAssets(value).pipe(finalize((): void => this.isLoadingAssets.set(false)))
	}

	private _listenSearchQuery(): void {
		this._searchQuery$
			.pipe(
				takeUntil(this._destroy$),
				debounceTime(500),
				distinctUntilChanged(),
				switchMap((query: string | null): Observable<SearchAssetsPaginationInterface> => {
					if (query && query.trim()) {
						return this._searchAssets$(query)
					} else {
						this.searchAssetsResponse.set(null)
						return EMPTY
					}
				}),
			)
			.subscribe((response: SearchAssetsPaginationInterface): void => {
				this.searchAssetsResponse.set({ ...response, assets: response.assets.slice(0, 7) })
			})
	}

	protected checkHasAssetInWatchList(asset: SearchAssetsItemInterface): boolean {
		return !!this.watchList()?.assets.find((item: WatchListAssetInterface): boolean => item.symbol === asset.symbol)
	}

	protected deleteAssetFromWatchList(asset: SearchAssetsItemInterface): void {
		this.symbolsLoading.set([...this.symbolsLoading(), asset.symbol])
		this._watchListService
			.deleteAsset(asset.symbol)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(): void => {
					this._alertService.success(
						this._translateService.instant('searchAssets.deleteSuccess', { symbol: asset.symbol }),
					)
					this.symbolsLoading.set(this.symbolsLoading().filter((s: string): boolean => s !== asset.symbol))
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error.message ||
							this._translateService.instant('searchAssets.deleteFail', { symbol: asset.symbol }),
					)
					this.symbolsLoading.set(this.symbolsLoading().filter((s: string): boolean => s !== asset.symbol))
				},
			)
	}

	protected addAssetToWatchList(asset: SearchAssetsItemInterface): void {
		this.symbolsLoading.set([...this.symbolsLoading(), asset.symbol])
		this._watchListService
			.addAsset(asset.symbol)
			.pipe(take(1))
			.subscribe(
				(): void => {
					this._watchListService
						.getWatchList()
						.pipe(take(1))
						.subscribe((): void => {
							this._alertService.success(
								this._translateService.instant('searchAssets.addSuccess', { symbol: asset.symbol }),
							)
							this.symbolsLoading.set(
								this.symbolsLoading().filter((s: string): boolean => s !== asset.symbol),
							)
						})
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error.message ||
							this._translateService.instant('searchAssets.addFail', { symbol: asset.symbol }),
					)
					this.symbolsLoading.set(this.symbolsLoading().filter((s: string): boolean => s !== asset.symbol))
				},
			)
	}

	protected selectAsset(asset: SearchAssetsItemInterface): void {
		this.onSelectAsset.emit(asset)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['searchValue']) {
			this._searchQuery$.next(this.searchValue())
		}
	}

	ngOnInit(): void {
		this._launchWatchList()
		this._listenSearchQuery()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
